import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { agriculturalAPI } from '../services/api';
import {
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  AlertCircle,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Maximize2,
  BarChart3,
  Activity,
  Scale,
  ChevronDown,
  X,
} from 'lucide-react';
import { RootState } from '../store/store';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const INDEX_NAME = 'import_export_values';

interface FilterValue {
  value: string;
  count: number;
}

interface TradeFilters {
  country?: string;
  item?: string;
  fromYear?: string;
  toYear?: string;
}

interface TradeData {
  year: number;
  exportValue: number;
  exportQuantity: number;
  importValue: number;
  importQuantity: number;
  balance: number;
  exportPricePerTon: number;
  importPricePerTon: number;
}

interface YearlyBreakdown {
  year: number;
  exports: { value: number; quantity: number };
  imports: { value: number; quantity: number };
  balance: number;
  growth: number | null;
}

const TradeAnalytics: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [filterValues, setFilterValues] = useState<{ [key: string]: FilterValue[] }>({});
  const [filters, setFilters] = useState<TradeFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [activeView, setActiveView] = useState<'flow' | 'compare' | 'balance'>('flow');
  const [compareViewMode, setCompareViewMode] = useState<'value' | 'quantity'>('value');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({});
  const [expandedModal, setExpandedModal] = useState<string | null>(null);

  const [tradeData, setTradeData] = useState<TradeData[]>([]);
  const [yearlyBreakdown, setYearlyBreakdown] = useState<YearlyBreakdown[]>([]);
  const [totalExports, setTotalExports] = useState({ value: 0, quantity: 0 });
  const [totalImports, setTotalImports] = useState({ value: 0, quantity: 0 });
  const [tradeBalance, setTradeBalance] = useState(0);
  const [exportGrowth, setExportGrowth] = useState<number | null>(null);
  const [tradeRatio, setTradeRatio] = useState<number | null>(null);
  const [avgExportPrice, setAvgExportPrice] = useState<number | null>(null);
  const [avgImportPrice, setAvgImportPrice] = useState<number | null>(null);

  useEffect(() => {
    loadFilterValues();
  }, []);

  const loadFilterValues = async () => {
    setLoadingFilters(true);
    try {
      const allFilterValues: { [key: string]: FilterValue[] } = {};
      
      // Load Area values using getIndexTerms
      try {
        const areaRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
          field: 'area',
          size: 300,
        });
        if (areaRes.data.success && areaRes.data.data && Array.isArray(areaRes.data.data)) {
          const validAreas = areaRes.data.data.filter((term: FilterValue) => 
            term.value && term.value.trim() !== ''
          );
          if (validAreas.length > 0) {
            allFilterValues['Area'] = validAreas;
            allFilterValues['area'] = validAreas;
            allFilterValues['countries'] = validAreas;
          }
        }
      } catch (e) {
        console.error('Failed to load area terms:', e);
      }

      // Load Item values using getIndexTerms
      try {
        const itemRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
          field: 'item',
          size: 500,
        });
        if (itemRes.data.success && itemRes.data.data && Array.isArray(itemRes.data.data)) {
          const validItems = itemRes.data.data.filter((term: FilterValue) => 
            term.value && term.value.trim() !== ''
          );
          if (validItems.length > 0) {
            allFilterValues['Item'] = validItems;
            allFilterValues['item'] = validItems;
            allFilterValues['items'] = validItems;
          }
        }
      } catch (e) {
        console.error('Failed to load item terms:', e);
      }

      // Load Year values using getIndexTerms - try multiple field name variations
      const yearFieldNames = ['year', 'Year', 'Year Code'];
      let yearsLoaded = false;
      
      for (const fieldName of yearFieldNames) {
        if (yearsLoaded) break;
        try {
          const yearRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
            field: fieldName,
            size: 100,
          });
          if (yearRes.data.success && yearRes.data.data && Array.isArray(yearRes.data.data)) {
            const validYears = yearRes.data.data
              .filter((term: FilterValue) => {
                const val = term.value;
                if (!val) return false;
                const yearNum = parseInt(String(val));
                return !isNaN(yearNum) && yearNum >= 1900 && yearNum <= 2100;
              })
              .map((term: FilterValue) => ({
                ...term,
                value: String(term.value), // Ensure year is a string
              }))
              .sort((a: FilterValue, b: FilterValue) => {
                const yearA = parseInt(a.value);
                const yearB = parseInt(b.value);
                return yearB - yearA; // Sort descending (newest first)
              });
            if (validYears.length > 0) {
              allFilterValues['Year'] = validYears;
              allFilterValues['year'] = validYears;
              allFilterValues['years'] = validYears;
              yearsLoaded = true;
              console.log(`Successfully loaded ${validYears.length} years from field: ${fieldName}`);
            }
          }
        } catch (e) {
          console.error(`Failed to load year terms from field ${fieldName}:`, e);
        }
      }

      // Also load other filter values from getAllIndexFilterValues as fallback
      try {
        const response = await agriculturalAPI.getAllIndexFilterValues(INDEX_NAME);
        if (response.data.success) {
          for (const fieldName of Object.keys(response.data.data.filterValues)) {
            const values = response.data.data.filterValues[fieldName]?.values || [];
            const validValues = values.filter((v: FilterValue) => 
              v.value && v.value.trim() !== ''
            );
            if (validValues.length > 0) {
              allFilterValues[fieldName] = validValues;
              const lowerFieldName = fieldName.toLowerCase();
              if (!allFilterValues[lowerFieldName]) {
                allFilterValues[lowerFieldName] = validValues;
              }
              if (fieldName === 'Area' || fieldName === 'area') {
                if (!allFilterValues['countries']) {
                  allFilterValues['countries'] = validValues;
                }
              }
              if (fieldName === 'Item' || fieldName === 'item') {
                if (!allFilterValues['items']) {
                  allFilterValues['items'] = validValues;
                }
              }
              // If years not loaded yet, try to load from this response
              if (!yearsLoaded && (fieldName === 'Year' || fieldName === 'year' || fieldName === 'Year Code')) {
                const yearValues = validValues
                  .filter((v: FilterValue) => {
                    const yearNum = parseInt(String(v.value));
                    return !isNaN(yearNum) && yearNum >= 1900 && yearNum <= 2100;
                  })
                  .map((v: FilterValue) => ({
                    ...v,
                    value: String(v.value),
                  }))
                  .sort((a: FilterValue, b: FilterValue) => {
                    const yearA = parseInt(a.value);
                    const yearB = parseInt(b.value);
                    return yearB - yearA;
                  });
                if (yearValues.length > 0) {
                  allFilterValues['Year'] = yearValues;
                  allFilterValues['year'] = yearValues;
                  allFilterValues['years'] = yearValues;
                  yearsLoaded = true;
                  console.log(`Successfully loaded ${yearValues.length} years from getAllIndexFilterValues: ${fieldName}`);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to load other filter values:', err);
      }

      // Ensure countries and items are set
      if (!allFilterValues['countries'] && allFilterValues['Area']) {
        allFilterValues['countries'] = allFilterValues['Area'];
      }
      if (!allFilterValues['countries'] && allFilterValues['area']) {
        allFilterValues['countries'] = allFilterValues['area'];
      }
      if (!allFilterValues['items'] && allFilterValues['Item']) {
        allFilterValues['items'] = allFilterValues['Item'];
      }
      if (!allFilterValues['items'] && allFilterValues['item']) {
        allFilterValues['items'] = allFilterValues['item'];
      }

      setFilterValues(allFilterValues);
      console.log('Filter values loaded:', {
        countries: allFilterValues.countries?.length || 0,
        items: allFilterValues.items?.length || 0,
        years: allFilterValues.years?.length || 0,
        allKeys: Object.keys(allFilterValues),
      });
    } catch (err: any) {
      console.error('Error loading filter values:', err);
      setError(`Failed to load filter values: ${err.response?.data?.message || err.message}`);
      setFilterValues({});
    } finally {
      setLoadingFilters(false);
    }
  };

  // Auto-set default filters
  useEffect(() => {
    if (Object.keys(filterValues).length > 0 && tradeData.length === 0 && !filters.country && !filters.item) {
      // Try to find Tunisia in the countries list, otherwise use first available
      const countryList = filterValues.countries || filterValues['Area'] || filterValues['area'] || [];
      const tunisiaCountry = countryList.find((country: FilterValue) => 
        country.value && country.value.toLowerCase().includes('tunisia')
      );
      const defaultCountry = tunisiaCountry?.value || countryList[0]?.value;
      const defaultItem = filterValues.items?.[0]?.value || filterValues['Item']?.[0]?.value;
      
      if (defaultCountry && defaultItem) {
        const newFilters: TradeFilters = {
          country: defaultCountry,
          item: defaultItem,
        };
        setFilters(newFilters);
      }
    }
  }, [filterValues]);

  // Auto-visualize when default filters are set
  useEffect(() => {
    if (filters.country && filters.item && tradeData.length === 0 && !loading) {
      setInitialLoading(true);
      const timer = setTimeout(() => {
        handleVisualize();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [filters.country, filters.item]);

  const handleVisualize = async () => {
    if (!filters.country || !filters.item) {
      setError('Please select both Country and Item');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build search params with year filters
      const buildParams = (element: string) => {
        const params: any = {
          limit: 10000,
          area: filters.country,
          item: filters.item,
          element: element,
        };
        if (filters.fromYear) {
          params.fromYear = filters.fromYear;
        }
        if (filters.toYear) {
          params.toYear = filters.toYear;
        }
        return params;
      };

      const [exportValueRes, exportQuantityRes, importValueRes, importQuantityRes] = await Promise.all([
        agriculturalAPI.searchIndex(INDEX_NAME, buildParams('Export value')).catch(err => ({ data: { success: false, data: [], message: err.message } })),
        agriculturalAPI.searchIndex(INDEX_NAME, buildParams('Export quantity')).catch(err => ({ data: { success: false, data: [], message: err.message } })),
        agriculturalAPI.searchIndex(INDEX_NAME, buildParams('Import value')).catch(err => ({ data: { success: false, data: [], message: err.message } })),
        agriculturalAPI.searchIndex(INDEX_NAME, buildParams('Import quantity')).catch(err => ({ data: { success: false, data: [], message: err.message } })),
      ]);

      const exportValueData = exportValueRes.data.success ? exportValueRes.data.data : [];
      const exportQuantityData = exportQuantityRes.data.success ? exportQuantityRes.data.data : [];
      const importValueData = importValueRes.data.success ? importValueRes.data.data : [];
      const importQuantityData = importQuantityRes.data.success ? importQuantityRes.data.data : [];

      const parseValue = (value: any): number => {
        if (value === null || value === undefined || value === '') return 0;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const parsed = parseFloat(value);
          return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
      };

      const dataByYear: { [year: number]: TradeData } = {};

      exportValueData.forEach((doc: any) => {
        const year = doc.year || doc['Year Code'] || doc['Year'];
        if (!year) return;
        const yearNum = typeof year === 'number' ? year : parseInt(String(year));
        if (isNaN(yearNum)) return;
        if (!dataByYear[yearNum]) {
          dataByYear[yearNum] = {
            year: yearNum,
            exportValue: 0,
            exportQuantity: 0,
            importValue: 0,
            importQuantity: 0,
            balance: 0,
            exportPricePerTon: 0,
            importPricePerTon: 0,
          };
        }
        const value = parseValue(doc.value || doc.Value);
        dataByYear[yearNum].exportValue += value * 1000;
      });

      exportQuantityData.forEach((doc: any) => {
        const year = doc.year || doc['Year Code'] || doc['Year'];
        if (!year) return;
        const yearNum = typeof year === 'number' ? year : parseInt(String(year));
        if (isNaN(yearNum)) return;
        if (!dataByYear[yearNum]) {
          dataByYear[yearNum] = {
            year: yearNum,
            exportValue: 0,
            exportQuantity: 0,
            importValue: 0,
            importQuantity: 0,
            balance: 0,
            exportPricePerTon: 0,
            importPricePerTon: 0,
          };
        }
        const value = parseValue(doc.value || doc.Value);
        dataByYear[yearNum].exportQuantity += value;
      });

      importValueData.forEach((doc: any) => {
        const year = doc.year || doc['Year Code'] || doc['Year'];
        if (!year) return;
        const yearNum = typeof year === 'number' ? year : parseInt(String(year));
        if (isNaN(yearNum)) return;
        if (!dataByYear[yearNum]) {
          dataByYear[yearNum] = {
            year: yearNum,
            exportValue: 0,
            exportQuantity: 0,
            importValue: 0,
            importQuantity: 0,
            balance: 0,
            exportPricePerTon: 0,
            importPricePerTon: 0,
          };
        }
        const value = parseValue(doc.value || doc.Value);
        dataByYear[yearNum].importValue += value * 1000;
      });

      importQuantityData.forEach((doc: any) => {
        const year = doc.year || doc['Year Code'] || doc['Year'];
        if (!year) return;
        const yearNum = typeof year === 'number' ? year : parseInt(String(year));
        if (isNaN(yearNum)) return;
        if (!dataByYear[yearNum]) {
          dataByYear[yearNum] = {
            year: yearNum,
            exportValue: 0,
            exportQuantity: 0,
            importValue: 0,
            importQuantity: 0,
            balance: 0,
            exportPricePerTon: 0,
            importPricePerTon: 0,
          };
        }
        const value = parseValue(doc.value || doc.Value);
        dataByYear[yearNum].importQuantity += value;
      });

      const processedData: TradeData[] = Object.values(dataByYear)
        .map((data) => {
          const balance = data.exportValue - data.importValue;
          const exportPricePerTon = data.exportQuantity > 0 ? data.exportValue / data.exportQuantity : 0;
          const importPricePerTon = data.importQuantity > 0 ? data.importValue / data.importQuantity : 0;
          return {
            ...data,
            balance,
            exportPricePerTon,
            importPricePerTon,
          };
        })
        .sort((a, b) => a.year - b.year);

      setTradeData(processedData);

      const totalExp = {
        value: processedData.reduce((sum, d) => sum + d.exportValue, 0),
        quantity: processedData.reduce((sum, d) => sum + d.exportQuantity, 0),
      };
      const totalImp = {
        value: processedData.reduce((sum, d) => sum + d.importValue, 0),
        quantity: processedData.reduce((sum, d) => sum + d.importQuantity, 0),
      };

      setTotalExports(totalExp);
      setTotalImports(totalImp);
      setTradeBalance(totalExp.value - totalImp.value);

      if (processedData.length >= 2) {
        const firstYear = processedData[0];
        const lastYear = processedData[processedData.length - 1];
        if (firstYear.exportValue > 0) {
          const growth = ((lastYear.exportValue - firstYear.exportValue) / firstYear.exportValue) * 100;
          setExportGrowth(growth);
        }
      }

      if (totalImp.value > 0) {
        setTradeRatio(totalExp.value / totalImp.value);
      } else if (totalExp.value > 0) {
        setTradeRatio(Infinity);
      }

      const validExportPrices = processedData.filter((d) => d.exportPricePerTon > 0);
      const validImportPrices = processedData.filter((d) => d.importPricePerTon > 0);

      if (validExportPrices.length > 0) {
        const avgExpPrice = validExportPrices.reduce((sum, d) => sum + d.exportPricePerTon, 0) / validExportPrices.length;
        setAvgExportPrice(avgExpPrice);
      }

      if (validImportPrices.length > 0) {
        const avgImpPrice = validImportPrices.reduce((sum, d) => sum + d.importPricePerTon, 0) / validImportPrices.length;
        setAvgImportPrice(avgImpPrice);
      }

      const breakdown: YearlyBreakdown[] = processedData.map((data, index) => {
        const prevYear = index > 0 ? processedData[index - 1] : null;
        const growth = prevYear && prevYear.exportValue > 0
          ? ((data.exportValue - prevYear.exportValue) / prevYear.exportValue) * 100
          : null;
        return {
          year: data.year,
          exports: { value: data.exportValue, quantity: data.exportQuantity },
          imports: { value: data.importValue, quantity: data.importQuantity },
          balance: data.balance,
          growth,
        };
      });

      setYearlyBreakdown(breakdown);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load trade data');
      console.error('Error loading trade data:', err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
  };

  const toggleCardExpand = (cardId: string) => {
    setExpandedModal(cardId);
  };

  const closeModal = () => {
    setExpandedModal(null);
  };

  const donutData = [
    { name: 'Exports', value: totalExports.value, percentage: totalExports.value + totalImports.value > 0 ? (totalExports.value / (totalExports.value + totalImports.value)) * 100 : 0 },
    { name: 'Imports', value: totalImports.value, percentage: totalExports.value + totalImports.value > 0 ? (totalImports.value / (totalExports.value + totalImports.value)) * 100 : 0 },
  ];

  const COLORS = ['#10b981', '#ef4444'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-sm border-b border-gray-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Trade Analytics</h1>
              <p className="text-sm text-gray-600 mt-1">
                Import & Export flows for {filters.item || 'Select a product'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setActiveView('flow')}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                    activeView === 'flow'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Activity className="w-4 h-4" />
                  Flow
                </button>
                <button
                  onClick={() => setActiveView('compare')}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                    activeView === 'compare'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Compare
                </button>
                <button
                  onClick={() => setActiveView('balance')}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-colors ${
                    activeView === 'balance'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Scale className="w-4 h-4" />
                  Balance
                </button>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium border transition-colors ${
                  showFilters
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 animate-in slide-in-from-top-2 duration-200">
              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <label className="text-xs text-gray-600 font-medium">Country</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={filters.country || ''}
                      onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                      disabled={loadingFilters}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Country</option>
                      {filterValues.countries?.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.value} ({country.count})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-1 min-w-[200px]">
                  <label className="text-xs text-gray-600 font-medium">Product</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={filters.item || ''}
                      onChange={(e) => setFilters({ ...filters, item: e.target.value })}
                      disabled={loadingFilters}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Product</option>
                      {filterValues.items?.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.value} ({item.count})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-1 min-w-[150px]">
                  <label className="text-xs text-gray-600 font-medium">From Year</label>
                  <select
                    value={filters.fromYear || ''}
                    onChange={(e) => setFilters({ ...filters, fromYear: e.target.value })}
                    disabled={loadingFilters}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">All Years</option>
                    {filterValues.years && filterValues.years.length > 0 ? (
                      filterValues.years.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.value}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No years available</option>
                    )}
                  </select>
                </div>
                <div className="flex flex-col gap-1 min-w-[150px]">
                  <label className="text-xs text-gray-600 font-medium">To Year</label>
                  <select
                    value={filters.toYear || ''}
                    onChange={(e) => setFilters({ ...filters, toYear: e.target.value })}
                    disabled={loadingFilters}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  >
                    <option value="">All Years</option>
                    {filterValues.years && filterValues.years.length > 0 ? (
                      filterValues.years.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.value}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No years available</option>
                    )}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleVisualize}
                    disabled={loading || !filters.country || !filters.item}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Visualize
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {tradeData.length === 0 && initialLoading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <RefreshCw className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading data...</h3>
            <p className="text-gray-600">Setting up default filters and fetching data.</p>
          </div>
        )}
        {tradeData.length === 0 && !loading && !initialLoading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select a country and product to view trade analytics</p>
          </div>
        )}

        {tradeData.length > 0 && (
          <>
            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-emerald-500 p-4">
                <div className="flex items-center gap-2 text-emerald-500 mb-2">
                  <ArrowUpRight className="w-4 h-4" />
                  <span className="text-xs font-medium">Total Exports</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalExports.value)}</p>
                <p className="text-xs text-gray-500 mt-1">{formatNumber(totalExports.quantity)} tonnes</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-rose-500 p-4">
                <div className="flex items-center gap-2 text-rose-500 mb-2">
                  <ArrowDownLeft className="w-4 h-4" />
                  <span className="text-xs font-medium">Total Imports</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalImports.value)}</p>
                <p className="text-xs text-gray-500 mt-1">{formatNumber(totalImports.quantity)} tonnes</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-blue-500 p-4">
                <div className="flex items-center gap-2 text-blue-500 mb-2">
                  <Scale className="w-4 h-4" />
                  <span className="text-xs font-medium">Trade Balance</span>
                </div>
                <p className={`text-2xl font-bold ${tradeBalance >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {tradeBalance >= 0 ? '+' : ''}{formatCurrency(Math.abs(tradeBalance))}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {tradeBalance >= 0 ? 'Surplus' : 'Deficit'}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-amber-500 p-4">
                <div className="flex items-center gap-2 text-amber-500 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Export Growth</span>
                </div>
                {exportGrowth !== null ? (
                  <>
                    <p className={`text-2xl font-bold ${exportGrowth >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {exportGrowth >= 0 ? '+' : ''}{exportGrowth.toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {tradeData[0]?.year} → {tradeData[tradeData.length - 1]?.year}
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-gray-400">N/A</p>
                )}
              </div>
            </div>

            {/* Main Visualization Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Primary Chart - Takes 2 columns */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {activeView === 'flow' ? 'Trade Flow Over Time' :
                       activeView === 'compare' ? 'Import vs Export Comparison' :
                       'Trade Balance Analysis'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {filters.country} • {filters.item} • {tradeData[0]?.year}-{tradeData[tradeData.length - 1]?.year}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleCardExpand('main-chart')}
                    className="text-gray-400 hover:text-gray-600 flex items-center gap-2 text-sm"
                  >
                    <span className="text-xs">Click to expand</span>
                    <Maximize2 className="w-5 h-5" />
                  </button>
                </div>

                {activeView === 'flow' && (
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={tradeData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          labelFormatter={(label) => `Year: ${label}`}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="exportValue"
                          name="Export Value"
                          stackId="1"
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.6}
                        />
                        <Area
                          type="monotone"
                          dataKey="importValue"
                          name="Import Value"
                          stackId="1"
                          stroke="#ef4444"
                          fill="#ef4444"
                          fillOpacity={0.6}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {activeView === 'compare' && (
                  <>
                    <div className="mb-4 flex gap-2">
                      <button
                        onClick={() => setCompareViewMode('value')}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                          compareViewMode === 'value'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        $ Value
                      </button>
                      <button
                        onClick={() => setCompareViewMode('quantity')}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                          compareViewMode === 'quantity'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Quantity
                      </button>
                    </div>
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={tradeData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(value) => compareViewMode === 'value' ? formatCurrency(value) : formatNumber(value)} />
                          <YAxis dataKey="year" type="category" width={60} />
                          <Tooltip
                            formatter={(value: number) => compareViewMode === 'value' ? formatCurrency(value) : `${formatNumber(value)} t`}
                            labelFormatter={(label) => `Year: ${label}`}
                          />
                          <Legend />
                          {compareViewMode === 'value' ? (
                            <>
                              <Bar dataKey="exportValue" name="Export" fill="#10b981" />
                              <Bar dataKey="importValue" name="Import" fill="#ef4444" />
                            </>
                          ) : (
                            <>
                              <Bar dataKey="exportQuantity" name="Export" fill="#10b981" />
                              <Bar dataKey="importQuantity" name="Import" fill="#ef4444" />
                            </>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}

                {activeView === 'balance' && (
                  <div className="flex items-start gap-8">
                    {/* Left: Donut Chart with Balance */}
                    <div className="flex-1 flex flex-col items-center">
                      <div className="relative w-full" style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={donutData}
                              cx="50%"
                              cy="50%"
                              innerRadius={80}
                              outerRadius={120}
                              dataKey="value"
                              startAngle={90}
                              endAngle={-270}
                            >
                              {donutData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* Center Text Overlay */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <div className="text-sm text-gray-600 font-medium mb-1">Balance</div>
                          <div className={`text-3xl font-bold ${tradeBalance >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {tradeBalance >= 0 ? '+' : ''}{formatCurrency(Math.abs(tradeBalance))}
                          </div>
                        </div>
                      </div>
                      {/* Legend */}
                      <div className="flex items-center gap-6 mt-6">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
                          <span className="text-sm text-gray-600">
                            Exports: {donutData[0].percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-rose-500"></div>
                          <span className="text-sm text-gray-600">
                            Imports: {donutData[1].percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Right: Yearly Trade Balance */}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Yearly Trade Balance</h3>
                      <div className="space-y-3">
                        {tradeData.slice(0, 5).map((data) => {
                          const maxBalance = Math.max(...tradeData.map(d => Math.abs(d.balance)));
                          const barWidth = maxBalance > 0 ? (Math.abs(data.balance) / maxBalance) * 100 : 0;
                          return (
                            <div key={data.year} className="flex items-center gap-4">
                              <div className="w-14 text-sm font-medium text-gray-600">{data.year}</div>
                              <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 relative h-6 bg-gray-100 rounded overflow-hidden">
                                  {data.balance !== 0 && (
                                    <div 
                                      className={`h-full ${
                                        data.balance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'
                                      }`} 
                                      style={{ width: `${barWidth}%` }}
                                    ></div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 min-w-[110px]">
                                  <span className={`text-sm font-semibold ${
                                    data.balance >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                  }`}>
                                    {data.balance >= 0 ? '+' : ''}{formatCurrency(Math.abs(data.balance))}
                                  </span>
                                  {data.balance >= 0 && (
                                    <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {tradeData.length > 5 && (
                          <div className="text-center pt-2">
                            <button
                              onClick={() => toggleCardExpand('main-chart')}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              Show all {tradeData.length} years →
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Side Panel */}
              <div className="space-y-4">
                {/* Value vs Quantity Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Value vs Quantity</h3>
                      <p className="text-sm text-gray-600">Unit price analysis</p>
                    </div>
                    <button
                      onClick={() => toggleCardExpand('price-analysis')}
                      className="text-gray-400 hover:text-gray-600 flex items-center gap-2 text-sm"
                    >
                      <span className="text-xs">Click to expand</span>
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-emerald-500" />
                        </div>
                        <span className="text-sm">Export Price/t</span>
                      </div>
                      <span className="font-bold text-emerald-500">
                        {avgExportPrice ? `$${formatNumber(avgExportPrice)}` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-rose-500" />
                        </div>
                        <span className="text-sm">Import Price/t</span>
                      </div>
                      <span className="font-bold text-rose-500">
                        {avgImportPrice ? `$${formatNumber(avgImportPrice)}` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Trade Ratio Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Trade Ratio</h3>
                      <p className="text-sm text-gray-600">Export to Import ratio</p>
                    </div>
                    <button
                      onClick={() => toggleCardExpand('trade-ratio')}
                      className="text-gray-400 hover:text-gray-600 flex items-center gap-2 text-sm"
                    >
                      <span className="text-xs">Click to expand</span>
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-24 h-24 mb-4">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="8"
                          strokeDasharray={`${tradeRatio && tradeRatio !== Infinity ? (totalExports.value / (totalExports.value + totalImports.value)) * 251.2 : 251.2} 251.2`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xl font-bold text-blue-600">
                          {tradeRatio !== null
                            ? tradeRatio === Infinity
                              ? '∞'
                              : `${formatNumber(tradeRatio)}x`
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 text-center">
                      {tradeRatio !== null && tradeRatio !== Infinity
                        ? `Exports are ${formatNumber(tradeRatio)}x imports`
                        : 'Exports significantly exceed imports'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Year by Year Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Year by Year Breakdown</h2>
                <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
                  <option>Show All Years</option>
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {yearlyBreakdown.map((year, index) => (
                  <div
                    key={year.year}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-lg font-semibold text-gray-900">{year.year}</div>
                      <div
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          year.balance >= 0
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {year.balance >= 0 ? 'Surplus' : 'Deficit'}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                          <div className="text-sm text-gray-600">Exports</div>
                        </div>
                        <div className="text-lg font-semibold text-emerald-600">
                          {formatCurrency(year.exports.value)}
                        </div>
                        <div className="text-sm text-gray-500">{formatNumber(year.exports.quantity)} t</div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowDownLeft className="w-4 h-4 text-rose-500" />
                          <div className="text-sm text-gray-600">Imports</div>
                        </div>
                        <div className="text-lg font-semibold text-rose-600">
                          {formatCurrency(year.imports.value)}
                        </div>
                        <div className="text-sm text-gray-500">{formatNumber(year.imports.quantity)} t</div>
                      </div>
                      <div className="pt-2 border-t border-gray-200">
                        <div className="text-sm text-gray-600 mb-1">Balance</div>
                        <div
                          className={`text-lg font-semibold ${
                            year.balance >= 0 ? 'text-emerald-600' : 'text-rose-600'
                          }`}
                        >
                          {formatCurrency(Math.abs(year.balance))}
                        </div>
                        {year.growth !== null && (
                          <div className="text-sm text-gray-500 mt-1">
                            {year.growth >= 0 ? '+' : ''}
                            {year.growth.toFixed(1)}% vs {yearlyBreakdown[index - 1]?.year || 'previous'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Expanded Modals */}
        {expandedModal === 'main-chart' && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {activeView === 'flow' ? 'Trade Flow Over Time' :
                     activeView === 'compare' ? 'Import vs Export Comparison' :
                     'Trade Balance Analysis'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {filters.country} • {filters.item} • {tradeData[0]?.year}-{tradeData[tradeData.length - 1]?.year}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              <div className="p-6">
                {activeView === 'flow' && (
                  <div className="h-[70vh]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={tradeData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                        <Tooltip
                          formatter={(value: number) => formatCurrency(value)}
                          labelFormatter={(label) => `Year: ${label}`}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="exportValue"
                          name="Export Value"
                          stackId="1"
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.6}
                        />
                        <Area
                          type="monotone"
                          dataKey="importValue"
                          name="Import Value"
                          stackId="1"
                          stroke="#ef4444"
                          fill="#ef4444"
                          fillOpacity={0.6}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {activeView === 'compare' && (
                  <>
                    <div className="mb-4 flex gap-2">
                      <button
                        onClick={() => setCompareViewMode('value')}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                          compareViewMode === 'value'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        $ Value
                      </button>
                      <button
                        onClick={() => setCompareViewMode('quantity')}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                          compareViewMode === 'quantity'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Quantity
                      </button>
                    </div>
                    <div className="h-[70vh]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={tradeData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(value) => compareViewMode === 'value' ? formatCurrency(value) : formatNumber(value)} />
                          <YAxis dataKey="year" type="category" width={60} />
                          <Tooltip
                            formatter={(value: number) => compareViewMode === 'value' ? formatCurrency(value) : `${formatNumber(value)} t`}
                            labelFormatter={(label) => `Year: ${label}`}
                          />
                          <Legend />
                          {compareViewMode === 'value' ? (
                            <>
                              <Bar dataKey="exportValue" name="Export" fill="#10b981" />
                              <Bar dataKey="importValue" name="Import" fill="#ef4444" />
                            </>
                          ) : (
                            <>
                              <Bar dataKey="exportQuantity" name="Export" fill="#10b981" />
                              <Bar dataKey="importQuantity" name="Import" fill="#ef4444" />
                            </>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}

                {activeView === 'balance' && (
                  <div className="flex items-start gap-8">
                    <div className="flex-1 flex flex-col items-center relative">
                      <div className="relative w-full" style={{ height: '400px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={donutData}
                              cx="50%"
                              cy="50%"
                              innerRadius={100}
                              outerRadius={150}
                              dataKey="value"
                              startAngle={90}
                              endAngle={-270}
                            >
                              {donutData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <div className="text-sm text-gray-600 font-medium mb-1">Balance</div>
                          <div className={`text-4xl font-bold ${tradeBalance >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {tradeBalance >= 0 ? '+' : ''}{formatCurrency(Math.abs(tradeBalance))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 mt-6">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-emerald-500"></div>
                          <span className="text-sm text-gray-600">
                            Exports: {donutData[0].percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-rose-500"></div>
                          <span className="text-sm text-gray-600">
                            Imports: {donutData[1].percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Yearly Trade Balance</h3>
                      <div className="space-y-3">
                        {tradeData.map((data) => {
                          const maxBalance = Math.max(...tradeData.map(d => Math.abs(d.balance)));
                          const barWidth = maxBalance > 0 ? (Math.abs(data.balance) / maxBalance) * 100 : 0;
                          return (
                            <div key={data.year} className="flex items-center gap-4">
                              <div className="w-14 text-sm font-medium text-gray-600">{data.year}</div>
                              <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 relative h-6 bg-gray-100 rounded overflow-hidden">
                                  {data.balance !== 0 && (
                                    <div 
                                      className={`h-full ${
                                        data.balance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'
                                      }`} 
                                      style={{ width: `${barWidth}%` }}
                                    ></div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 min-w-[110px]">
                                  <span className={`text-sm font-semibold ${
                                    data.balance >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                  }`}>
                                    {data.balance >= 0 ? '+' : ''}{formatCurrency(Math.abs(data.balance))}
                                  </span>
                                  {data.balance >= 0 && (
                                    <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {expandedModal === 'price-analysis' && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Value vs Quantity</h3>
                  <p className="text-sm text-gray-600 mt-1">Unit price analysis</p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-6 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-sm text-gray-600 mb-2">Average Export Price per Tonne</p>
                    <p className="text-4xl font-bold text-emerald-500">
                      {avgExportPrice ? `$${formatNumber(avgExportPrice)}` : 'N/A'}
                    </p>
                  </div>
                  <div className="p-6 rounded-lg bg-rose-500/10 border border-rose-500/20">
                    <p className="text-sm text-gray-600 mb-2">Average Import Price per Tonne</p>
                    <p className="text-4xl font-bold text-rose-500">
                      {avgImportPrice ? `$${formatNumber(avgImportPrice)}` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {expandedModal === 'trade-ratio' && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Trade Ratio</h3>
                  <p className="text-sm text-gray-600 mt-1">Export to Import ratio</p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              <div className="p-6">
                <div className="flex flex-col items-center justify-center">
                  <div className="relative w-64 h-64 mb-6">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="128"
                        cy="128"
                        r="100"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="16"
                      />
                      <circle
                        cx="128"
                        cy="128"
                        r="100"
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="16"
                        strokeDasharray={`${tradeRatio && tradeRatio !== Infinity ? (totalExports.value / (totalExports.value + totalImports.value)) * 628 : 628} 628`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-5xl font-bold text-blue-600">
                        {tradeRatio !== null
                          ? tradeRatio === Infinity
                            ? '∞'
                            : `${formatNumber(tradeRatio)}x`
                          : 'N/A'}
                      </span>
                      <span className="text-sm text-gray-600 mt-2">Export/Import</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 text-center">
                    {tradeRatio !== null && tradeRatio !== Infinity
                      ? `Exports are ${formatNumber(tradeRatio)}x imports`
                      : 'Exports significantly exceed imports'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeAnalytics;
